import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FaLeaf, FaUsers, FaChartLine, FaFileAlt, FaBalanceScale, FaHandshake, FaGlobeAmericas, FaRecycle, FaClipboardCheck, FaBullhorn, FaRegLightbulb } from "react-icons/fa";
import "bootstrap/dist/css/bootstrap.min.css";
import Header from "../components/Header";
import Footer from "../components/Footer";
import styled, { keyframes } from "styled-components";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideInLeft = keyframes`
  from { opacity: 0; transform: translateX(-100px); }
  to { opacity: 1; transform: translateX(0); }
`;

const slideInRight = keyframes`
  from { opacity: 0; transform: translateX(100px); }
  to { opacity: 1; transform: translateX(0); }
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(50px); }
  to { opacity: 1; transform: translateY(0); }
`;

const FAQContainer = styled.div`
  display: flex;
  gap: 40px;
  width: 100%;
  background: white;
  padding: 40px;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
  border-radius: 10px;

  @media (max-width: 768px) {
    flex-direction: column;
    padding: 20px;
  }
`;

const FAQContent = styled.div`
  flex: 1;
  animation: ${slideInRight} 1s ease-in-out;
`;

const FAQItem = styled.div`
  background: #ffffff;
  margin: 15px 0;
  border-bottom: 1px solid #d1d5db;
  overflow: hidden;
  transition: all 0.3s ease-in-out;
  opacity: 0;
  transform: translateY(20px);
  animation: ${fadeUp} 0.5s ease-in-out forwards;
  animation-delay: ${({ delay }) => delay || "0s"};

  &:hover {
    box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.1);
  }
`;

const Question = styled.div`
  background: #f9fafb;
  padding: 15px;
  font-size: 18px;
  font-weight: bold;
  color: #333;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: background 0.3s ease;

  &:hover {
    background: #eef1f6;
  }
`;

const Answer = styled.div`
  padding: 15px;
  font-size: 16px;
  color: #555;
  background: #ffffff;
  border-top: 1px solid #d1d5db;
  animation: ${fadeIn} 0.5s ease-in-out;
`;

const Icon = styled.span`
  font-size: 22px;
  font-weight: bold;
  color: #28a745;
  transition: transform 0.3s ease;
  ${({ isOpen }) => isOpen && "transform: rotate(180deg);"}
`;

const ServiceCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 8px 15px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  margin-bottom: 30px;
  animation: ${fadeUp} 0.5s ease-in-out forwards;
  animation-delay: ${({ delay }) => delay || "0s"};
  height: 100%;
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
  }
`;

const ServiceIcon = styled.div`
  font-size: 42px;
  color: ${props => props.color || "#28a745"};
  margin-bottom: 20px;
`;

const ServiceTitle = styled.h4`
  font-weight: 700;
  margin-bottom: 15px;
  color: #333;
`;

const ServiceDescription = styled.p`
  color: #666;
  font-size: 16px;
  line-height: 1.6;
`;

const AdvantageCard = styled.div`
  padding: 25px 15px;
  background: white;
  border-radius: 10px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  }
`;

const AdvantageIcon = styled.div`
  font-size: 48px;
  margin-bottom: 15px;
  color: ${props => props.color || "#28a745"};
`;

const Section = styled.section`
  padding: 80px 0;
  background: ${props => props.background || "white"};
`;

const RSEDeveloppementDurable = () => {
    const [activeIndex, setActiveIndex] = useState(null);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            const faqSection = document.getElementById("faq-section");
            if (faqSection) {
                const rect = faqSection.getBoundingClientRect();
                if (rect.top < window.innerHeight * 0.8 && rect.bottom > 0) {
                    setIsVisible(true);
                } else {
                    setIsVisible(false);
                }
            }
        };

        window.addEventListener("scroll", handleScroll);
        handleScroll();
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    const toggleFAQ = (index) => {
        setActiveIndex(activeIndex === index ? null : index);
    };

    const faqData = [
        {
            question: "Qu'est-ce que la RSE et pourquoi est-elle importante pour mon entreprise ?",
            answer: "La Responsabilité Sociétale des Entreprises (RSE) englobe l'ensemble des pratiques mises en œuvre par une entreprise pour respecter les principes du développement durable. Cela inclut les aspects environnementaux, sociaux et de gouvernance. La RSE est cruciale car elle permet d'améliorer l'image de votre entreprise, de fidéliser vos clients, d'attirer les talents, de réduire les risques et d'améliorer votre performance globale."
        },
        {
            question: "En quoi votre cabinet peut-il m'aider en matière de RSE ?",
            answer: "Nous proposons un accompagnement personnalisé, allant du diagnostic initial à la mise en œuvre de votre stratégie RSE, en passant par le reporting et la conformité réglementaire. Nous vous aidons à intégrer les enjeux RSE dans votre stratégie, à mesurer votre impact, à définir des objectifs concrets et à communiquer efficacement sur vos engagements."
        },
        {
            question: "Quels sont les principaux services que vous proposez ?",
            answer: "Nos services incluent : Diagnostic et évaluation RSE, Élaboration de stratégies RSE sur mesure, Mise en œuvre d'actions concrètes, Reporting et communication RSE, Veille réglementaire et conformité."
        },
        {
            question: "Mon entreprise est une PME, la RSE est-elle vraiment pertinente pour nous ?",
            answer: "Oui, absolument. La RSE concerne toutes les entreprises, quelle que soit leur taille. Les PME ont un rôle important à jouer dans la transition vers un modèle économique plus durable. En intégrant la RSE dans votre modèle d'entreprise, vous obtiendrez de nombreux avantages, une meilleure image, des économies de couts, une plus grande motivations de vos employés..."
        },
        {
            question: "Comment mesurez-vous l'impact RSE de mon entreprise ?",
            answer: "Nous utilisons des outils et des méthodologies éprouvés pour évaluer votre performance RSE, en tenant compte des normes et des référentiels en vigueur. Nous vous aidons à identifier les indicateurs clés de performance (KPI) pertinents pour votre secteur d'activité et à suivre leur évolution."
        },
        {
            question: "Quelles sont les obligations légales en matière de RSE ?",
            answer: "Les obligations légales varient en fonction de la taille de votre entreprise et de votre secteur d'activité. Nous vous aidons à comprendre les exigences applicables et à vous assurer de votre conformité. Ces obligations peuvent concerner le bilan gaz a effet de serre, la publications de donnée extra-financiere, la mise en place de plan de vigilance..."
        },
        {
            question: "Comment valoriser mes actions RSE auprès de mes parties prenantes ?",
            answer: "Nous vous aidons à élaborer un reporting RSE transparent et pertinent, conforme aux normes en vigueur. Nous vous conseillons sur les meilleures pratiques de communication pour valoriser vos engagements auprès de vos clients, fournisseurs, investisseurs et autres parties prenantes."
        }
    ];

    const servicesData = [
        {
            icon: <FaClipboardCheck />,
            title: "Diagnostic et évaluation RSE",
            description: "Mesurez votre impact environnemental, social et de gouvernance pour identifier vos leviers d'action et comprendre votre situation actuelle.",
            color: "#28a745"
        },
        {
            icon: <FaChartLine />,
            title: "Stratégie RSE sur mesure",
            description: "Définissons ensemble des objectifs ambitieux et concrets, alignés avec vos valeurs et votre secteur d'activité pour maximiser votre impact positif.",
            color: "#17a2b8"
        },
        {
            icon: <FaUsers />,
            title: "Mise en œuvre opérationnelle",
            description: "Traduisons vos engagements en actions concrètes, grâce à des outils et des méthodologies éprouvés qui impliquent toutes vos parties prenantes.",
            color: "#fd7e14"
        },
        {
            icon: <FaBullhorn />,
            title: "Reporting et communication",
            description: "Valorisez vos performances RSE auprès de vos parties prenantes, en toute transparence avec des rapports conformes aux standards internationaux.",
            color: "#6f42c1"
        },
        {
            icon: <FaBalanceScale />,
            title: "Veille réglementaire et conformité",
            description: "Assurez-vous de respecter les obligations légales et anticipez les évolutions à venir pour rester à la pointe des pratiques RSE.",
            color: "#dc3545"
        }
    ];

    const advantagesData = [
        {
            icon: <FaLeaf />,
            title: "Expertise RSE",
            color: "#28a745"
        },
        {
            icon: <FaHandshake />,
            title: "Accompagnement Personnalisé",
            color: "#17a2b8"
        },
        {
            icon: <FaUsers />,
            title: "Équipe Pluridisciplinaire",
            color: "#fd7e14"
        },
        {
            icon: <FaChartLine />,
            title: "Approche Pragmatique",
            color: "#dc3545"
        },
        {
            icon: <FaRegLightbulb />,
            title: "Outils Performants",
            color: "#6f42c1"
        }
    ];

    return (
        <>
            <Header />

            <section className="service-banner d-flex align-items-center">
                <div className="container-fluid px-4 px-md-5 text-white">
                    <div className="row">
                        <div className="col-lg-8">
                            <h1 className="fw-bold display-4 mb-3">RSE & Développement Durable</h1>
                            <p className="fs-5 mb-4">Construisons ensemble un avenir durable et responsable pour votre entreprise</p>
                            <nav aria-label="breadcrumb">
                                <ol className="breadcrumb">
                                    <li className="breadcrumb-item">
                                        <Link to="/" className="text-white text-decoration-none">
                                            Accueil
                                        </Link>{" "}
                                        /{" "}
                                        <Link to="/services" className="text-white text-decoration-none">
                                            Services
                                        </Link>
                                    </li>
                                    <li className="breadcrumb-item active text-white" aria-current="page">
                                        RSE & Développement Durable
                                    </li>
                                </ol>
                            </nav>
                        </div>
                    </div>
                </div>
            </section>

            <Section>
                <div className="container-fluid px-4 px-md-5">
                    <div className="row">
                        <div className="col-12">
                            <h2 className="fw-bold custom-title text-center mb-5">
                                Dans un monde en constante évolution, la Responsabilité Sociétale des Entreprises n'est plus une option, mais un impératif
                            </h2>
                            <p className="text-muted text-center fs-5 mb-5">
                                Chez CINEF-EC, nous sommes convaincus que la performance économique et l'engagement éthique vont de pair. C'est pourquoi nous avons développé une expertise pointue pour vous accompagner dans l'intégration de ces enjeux au cœur de votre stratégie.
                            </p>
                        </div>
                    </div>

                    <div className="row mb-5">
                        <div className="col-12">
                            <div className="bg-light p-5 rounded-3">
                                <h3 className="fw-bold custom-title mb-4">
                                    Pourquoi choisir CINEF-EC pour votre démarche RSE ?
                                </h3>
                                <div className="row">
                                    <div className="col-md-6 mb-4">
                                        <div className="d-flex">
                                            <div className="me-3">
                                                <FaChartLine className="fs-1 text-success" />
                                            </div>
                                            <div>
                                                <h5 className="fw-bold">Expertise comptable et financière</h5>
                                                <p className="text-muted">
                                                    Nous mettons notre expertise comptable et financière au service d'une démarche RSE performante et créatrice de valeur.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-4">
                                        <div className="d-flex">
                                            <div className="me-3">
                                                <FaHandshake className="fs-1 text-info" />
                                            </div>
                                            <div>
                                                <h5 className="fw-bold">Accompagnement personnalisé</h5>
                                                <p className="text-muted">
                                                    Nous adaptons notre approche à la taille et aux spécificités de votre entreprise pour des résultats concrets.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-4">
                                        <div className="d-flex">
                                            <div className="me-3">
                                                <FaUsers className="fs-1 text-warning" />
                                            </div>
                                            <div>
                                                <h5 className="fw-bold">Équipe pluridisciplinaire</h5>
                                                <p className="text-muted">
                                                    Notre équipe d'experts en RSE et développement durable vous apporte une vision globale et complémentaire.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-4">
                                        <div className="d-flex">
                                            <div className="me-3">
                                                <FaRegLightbulb className="fs-1 text-danger" />
                                            </div>
                                            <div>
                                                <h5 className="fw-bold">Approche pragmatique</h5>
                                                <p className="text-muted">
                                                    Notre approche est axée sur les résultats concrets et la création de valeur durable pour votre entreprise.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Section>

            <Section background="#f8f9fa">
                <div className="container-fluid px-4 px-md-5">
                    <div className="row mb-5">
                        <div className="col-12 text-center">
                            <h2 className="fw-bold mb-1">Notre approche globale et personnalisée</h2>
                            <div className="d-inline-block mb-4">
                                <div className="custom-underline mx-auto"></div>
                            </div>
                            <p className="text-muted fs-5 mb-5">Des solutions RSE adaptées à vos enjeux et à votre secteur d'activité</p>
                        </div>
                    </div>

                    <div className="row">
                        {servicesData.map((service, index) => (
                            <div className="col-md-6 col-lg-4 mb-4" key={index}>
                                <ServiceCard delay={`${index * 0.1}s`}>
                                    <ServiceIcon color={service.color}>{service.icon}</ServiceIcon>
                                    <ServiceTitle>{service.title}</ServiceTitle>
                                    <ServiceDescription>{service.description}</ServiceDescription>
                                </ServiceCard>
                            </div>
                        ))}
                    </div>
                </div>
            </Section>

            <Section>
                <div className="container-fluid px-4 px-md-5">
                    <div className="row justify-content-center mb-5">
                        <div className="col-12 col-md-10 col-lg-8">
                            <div className="call-to-action p-5 rounded-3 text-center">
                                <h3 className="fw-bold text-white mb-3">Construisons ensemble un avenir durable</h3>
                                <p className="text-white fs-5 mb-4">
                                    La RSE est un investissement dans l'avenir de votre entreprise. Elle vous permet de réduire vos coûts, d'attirer les talents, 
                                    de fidéliser vos clients et de vous différencier sur votre marché. Contactez-nous pour engager votre transition vers un modèle d'entreprise plus responsable.
                                </p>
                                <a href="/contact" className="btn btn-light btn-lg fw-bold px-4">Demander un diagnostic RSE</a>
                            </div>
                        </div>
                    </div>

                    <div className="row mt-5">
                        <div className="col-12 text-center mb-5">
                            <h3 className="fw-bold">Vos avantages</h3>
                            <div className="d-inline-block mb-4">
                                <div className="custom-underline mx-auto"></div>
                            </div>
                        </div>
                    </div>

                    <div className="row justify-content-center">
                        {advantagesData.map((item, index) => (
                            <div className="col-6 col-md-4 col-lg-2" key={index}>
                                <AdvantageCard>
                                    <AdvantageIcon color={item.color}>{item.icon}</AdvantageIcon>
                                    <p className="fw-bold fs-5 mb-0 text-center">{item.title}</p>
                                </AdvantageCard>
                            </div>
                        ))}
                    </div>
                </div>
            </Section>

            <Section background="#f8f9fa" id="faq-section" className={isVisible ? 'visible' : ''}>
                <div className="container-fluid px-4 px-md-5">
                    <div className="row mb-5">
                        <div className="col-12 text-center">
                            <h3 className="fw-bold">Questions Fréquentes sur la RSE</h3>
                            <div className="d-inline-block mb-4">
                                <div className="custom-underline mx-auto"></div>
                            </div>
                        </div>
                    </div>

                    <div className="row justify-content-center">
                        <div className="col-12 col-lg-10">
                            <FAQContainer>
                                <FAQContent>
                                    {faqData.map((faq, index) => (
                                        <FAQItem key={index} delay={`${index * 0.2}s`}>
                                            <Question onClick={() => toggleFAQ(index)}>
                                                {faq.question}
                                                <Icon isOpen={activeIndex === index}>{activeIndex === index ? "−" : "+"}</Icon>
                                            </Question>
                                            {activeIndex === index && <Answer>{faq.answer}</Answer>}
                                        </FAQItem>
                                    ))}
                                </FAQContent>
                            </FAQContainer>
                        </div>
                    </div>
                </div>
            </Section>

            <Footer />

            <style>{`
        .service-banner {
          background: linear-gradient(to right, rgba(0, 138, 68, 0.85), rgba(220, 53, 69, 0.85)),
                      url("/assets/images/image-rse.png") no-repeat center center;
          background-size: cover;
          background-position: center;
          height: 400px;
          display: flex;
          align-items: center;
        }

        .breadcrumb {
          background: transparent;
          font-size: 18px;
        }

        .breadcrumb-item a {
          color: white;
          font-weight: 500;
        }

        .custom-title {
          position: relative;
          padding-bottom: 15px;
        }

        .custom-title:after {
          content: '';
          position: absolute;
          left: 0;
          bottom: 0;
          width: 60px;
          height: 3px;
          background: linear-gradient(to right, #008a44, #dc3545);
        }

        .text-center .custom-title:after {
          left: 50%;
          transform: translateX(-50%);
        }

        .custom-underline {
          height: 4px;
          width: 70px;
          background: linear-gradient(to right, #008a44, #dc3545);
          margin-top: 10px;
        }

        .call-to-action {
          background: linear-gradient(to right, #008a44, #dc3545);
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
        }

        #faq-section {
          opacity: 0;
          transform: translateY(30px);
          transition: all 0.8s ease;
        }

        #faq-section.visible {
          opacity: 1;
          transform: translateY(0);
        }
        
        @media (max-width: 768px) {
          .service-banner {
            height: 350px;
          }
          
          .custom-title {
            font-size: 1.75rem;
          }
        }
      `}</style>
        </>
    );
};

export default RSEDeveloppementDurable;